import { memo, useEffect, useState } from 'react';

import { AnalyticEvent } from ':fat/analytics/events';
import { logGenericEvent } from ':fat/analytics/logActions';
import { validateToken } from ':fat/api/client';

import { clearAuthToken, getAuthToken, setAuthToken } from './tokenStore';

const COMMERCE_URL = process.env.COMMERCE_URL ?? 'https://beta.commerce.coinbase.com';

type AuthGateProps = {
  children: React.ReactElement;
};

export const AuthGate = memo(function AuthGate({ children }: AuthGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if token was already set (e.g. from a previous render)
    return getAuthToken() !== null;
  });

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('auth_token');

    if (token) {
      setAuthToken(token);

      // Strip token from URL to prevent leaking via referrer or browser history
      params.delete('auth_token');
      const cleanSearch = params.toString();
      const cleanUrl =
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
      window.history.replaceState(null, '', cleanUrl);

      logGenericEvent(AnalyticEvent.AuthTokenReceived);

      // Validate the token before granting access
      void validateToken().then((isValid) => {
        if (isValid) {
          setIsAuthenticated(true);
        } else {
          clearAuthToken();
          logGenericEvent(AnalyticEvent.AuthTokenExpired);
          window.location.assign(COMMERCE_URL);
        }
      });
    } else {
      logGenericEvent(AnalyticEvent.AuthTokenMissing);
      window.location.assign(COMMERCE_URL);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return children;
});
